import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const authApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Intercepteur pour ajouter le token aux requêtes
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Intercepteur pour gérer les erreurs d'authentification
authApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Token expiré ou invalide
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      
      // Éviter la boucle de redirection si on est déjà sur la page de login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

const authService = {
  // Connexion
  async login(email, password) {
    try {
      const response = await authApi.post('/auth/login', {
        email,
        password
      });
      
      const { access_token, user } = response.data;
      
      // Stocker le token et les infos utilisateur
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { token: access_token, user };
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur de connexion');
    }
  },

  // Inscription
  async register(email, password, full_name) {
    try {
      const response = await authApi.post('/auth/register', {
        email,
        password,
        full_name
      });
      
      const { access_token, user } = response.data;
      
      // Stocker le token et les infos utilisateur
      localStorage.setItem('authToken', access_token);
      localStorage.setItem('user', JSON.stringify(user));
      
      return { token: access_token, user };
    } catch (error) {
      throw new Error(error.response?.data?.error || 'Erreur d\'inscription');
    }
  },

  // Déconnexion
  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  // Vérifier si l'utilisateur est connecté
  isAuthenticated() {
    const token = localStorage.getItem('authToken');
    return !!token;
  },

  // Obtenir le token actuel
  getToken() {
    return localStorage.getItem('authToken');
  },

  // Obtenir les infos utilisateur
  getUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Vérifier si le token est expiré
  isTokenExpired() {
    const token = this.getToken();
    if (!token) return true;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch (error) {
      return true;
    }
  },

  // Rafraîchir le token (si nécessaire)
  async refreshToken() {
    try {
      const response = await authApi.post('/auth/refresh');
      const { token } = response.data;
      localStorage.setItem('authToken', token);
      return token;
    } catch (error) {
      this.logout();
      throw error;
    }
  }
};

export default authService; 